import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    FiPlus, FiRefreshCw, FiSearch, FiX, FiEdit3, FiChevronLeft, FiChevronRight,
    FiChevronUp, FiChevronDown, FiTrendingUp, FiTrendingDown, FiPieChart,
    FiDollarSign, FiCreditCard, FiLayers, FiCheckCircle, FiSlash,
} from "react-icons/fi";
import "../../Assets/Accounting.css";
import {
    getChartOfAccounts,
    createChartOfAccount,
    updateChartOfAccount,
} from "../../_services/chartOfAccounts.service";
import { T, StatCard, StatusBadge, Banner, FieldInput, SectionHeading } from "./Company/CompanyParts";
import { FIX_FIELDS_MESSAGE, requiredMessage, saveFailedMessage } from "../../_components/Shared/formErrors";

/* ══════════════════════════════════════════════
   Account type config — icon + color per type,
   reused for tabs, table icons, and the form.
══════════════════════════════════════════════ */
const TYPE_DEFS = [
    { value: "Asset", icon: FiTrendingUp, color: "rgb(37,99,235)" },
    { value: "Liability", icon: FiTrendingDown, color: "rgb(220,53,69)" },
    { value: "Equity", icon: FiPieChart, color: "rgb(124,58,237)" },
    { value: "Revenue", icon: FiDollarSign, color: "rgb(5,150,105)" },
    { value: "Expense", icon: FiCreditCard, color: "rgb(217,119,6)" },
];
const ACCOUNT_TYPES = TYPE_DEFS.map((t) => t.value);
const typeDef = (type) => TYPE_DEFS.find((t) => t.value === type) || TYPE_DEFS[0];

const emptyForm = {
    account_code: "",
    account_name: "",
    account_type: "Asset",
    parent_id: "",
    is_bank_account: false,
    is_active: true,
};

const ROWS_OPTIONS = [10, 25, 50];

export default function ChartOfAccounts() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [banner, setBanner] = useState({ type: "", msg: "" });
    const bannerTimerRef = useRef(null);

    // Filters / search / sort / pagination
    const [activeType, setActiveType] = useState("All");
    const [search, setSearch] = useState("");
    const [sortKey, setSortKey] = useState("account_code");
    const [sortDir, setSortDir] = useState("asc");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Modal / form state
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    /* Auto-hide banner, same pattern as Company page */
    useEffect(() => {
        if (!banner.msg) return;
        clearTimeout(bannerTimerRef.current);
        bannerTimerRef.current = setTimeout(() => setBanner({ type: "", msg: "" }), banner.type === "danger" ? 5000 : 3000);
        return () => clearTimeout(bannerTimerRef.current);
    }, [banner]);

    const load = async () => {
        setLoading(true);
        try {
            const res = await getChartOfAccounts();
            setRows(res?.data || res || []);
        } catch (e) {
            console.error(e);
            setBanner({ type: "danger", msg: "Failed to load accounts. Please try again." });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    /* ── Derived data ── */
    const metrics = useMemo(() => ({
        total: rows.length,
        bank: rows.filter((r) => r.is_bank_account).length,
        active: rows.filter((r) => r.is_active !== false).length,
        inactive: rows.filter((r) => r.is_active === false).length,
    }), [rows]);

    const typeCounts = useMemo(() => {
        const counts = {};
        ACCOUNT_TYPES.forEach((t) => { counts[t] = rows.filter((r) => r.account_type === t).length; });
        return counts;
    }, [rows]);

    const filtered = useMemo(() => {
        let list = rows;
        if (activeType !== "All") list = list.filter((r) => r.account_type === activeType);
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            list = list.filter((r) =>
                (r.account_code || "").toLowerCase().includes(q) ||
                (r.account_name || "").toLowerCase().includes(q)
            );
        }
        const sorted = [...list].sort((a, b) => {
            const av = (a[sortKey] ?? "").toString().toLowerCase();
            const bv = (b[sortKey] ?? "").toString().toLowerCase();
            if (av < bv) return sortDir === "asc" ? -1 : 1;
            if (av > bv) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [rows, activeType, search, sortKey, sortDir]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    useEffect(() => { setPage(1); }, [activeType, search, pageSize]);

    const toggleSort = (key) => {
        if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortKey(key); setSortDir("asc"); }
    };

    /* ── Modal handlers ── */
    const openAdd = () => {
        setEditId(null);
        setForm(emptyForm);
        setErrors({});
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setEditId(row.id);
        setForm({
            account_code: row.account_code || "",
            account_name: row.account_name || "",
            account_type: row.account_type || "Asset",
            parent_id: row.parent_id || "",
            is_bank_account: !!row.is_bank_account,
            is_active: row.is_active !== false,
        });
        setErrors({});
        setModalOpen(true);
    };

    const onFieldChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value, ...(name === "account_type" ? { parent_id: "" } : {}) }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleSave = async () => {
        const errs = {};
        if (!form.account_code.trim()) errs.account_code = requiredMessage("Account Code");
        if (!form.account_name.trim()) errs.account_name = requiredMessage("Account Name");
        setErrors(errs);
        if (Object.keys(errs).length) {
            setBanner({ type: "danger", msg: FIX_FIELDS_MESSAGE });
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form, parent_id: form.parent_id || null };
            if (editId) {
                await updateChartOfAccount(editId, payload);
                setBanner({ type: "success", msg: `Account "${form.account_name}" updated.` });
            } else {
                await createChartOfAccount(payload);
                setBanner({ type: "success", msg: `Account "${form.account_name}" created.` });
            }
            setModalOpen(false);
            load();
        } catch (e) {
            // Highlight the fields the server refused (e.g. a duplicate account_code).
            if (e?.fields) setErrors((prev) => ({ ...prev, ...e.fields }));
            setBanner({ type: "danger", msg: saveFailedMessage("Account", e) });
        } finally {
            setSaving(false);
        }
    };

    const activeDef = typeDef(form.account_type);

    return (
        <div style={{ padding: "20px 0", fontSize: T.fontSizeBase }}>
            {/* Header — matches Company page */}
            <div style={{ marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
                <div>
                    <h4 style={{ fontWeight: 700, color: T.textPrimary, margin: 0 }}>Chart of Accounts</h4>
                    <p style={{ fontSize: T.fontSizeXs, color: T.textMuted, margin: "3px 0 0" }}>
                        Manage the accounts used across your bookkeeping and payment vouchers.
                    </p>
                </div>
            </div>

            <Banner type={banner.type} msg={banner.msg} onClose={() => setBanner({ type: "", msg: "" })} />

            {/* Metrics */}
            <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: 12,
                marginBottom: 20,
            }}>
                <StatCard icon={FiLayers} color="rgb(37,99,235)" label="Total Accounts" value={metrics.total} />
                <StatCard icon={FiCreditCard} color="rgb(5,150,105)" label="Bank / Cash Accounts" value={metrics.bank} />
                <StatCard icon={FiCheckCircle} color="rgb(22,163,74)" label="Active" value={metrics.active} />
                <StatCard icon={FiSlash} color="rgb(148,163,184)" label="Inactive" value={metrics.inactive} />
            </div>

            {/* Card container — same border/radius/shadow language as Company page */}
            <div style={{
                background: T.cardBg, borderRadius: T.radius,
                border: `1.5px solid ${T.border}`, boxShadow: T.shadow,
                overflow: "hidden",
            }}>
                {/* Type filter tabs */}
                <div style={{
                    display: "flex", gap: 6, flexWrap: "wrap",
                    padding: "16px 20px 0", borderBottom: `1px solid ${T.border}`,
                }}>
                    {["All", ...ACCOUNT_TYPES].map((t) => {
                        const isActive = activeType === t;
                        const def = t === "All" ? null : typeDef(t);
                        const count = t === "All" ? rows.length : typeCounts[t] || 0;
                        return (
                            <button
                                key={t}
                                onClick={() => setActiveType(t)}
                                style={{
                                    display: "flex", alignItems: "center", gap: 6,
                                    padding: "8px 14px", marginBottom: -1,
                                    background: "transparent", border: "none",
                                    borderBottom: isActive ? `2px solid ${T.accent}` : "2px solid transparent",
                                    color: isActive ? T.accent : T.textMuted,
                                    fontSize: T.fontSizeXs, fontWeight: isActive ? 700 : 500,
                                    cursor: "pointer", whiteSpace: "nowrap",
                                }}
                            >
                                {def && <def.icon size={13} />}
                                {t}
                                <span style={{
                                    background: isActive ? "#eff6ff" : T.bg,
                                    color: isActive ? T.accent : T.textLight,
                                    borderRadius: 999, padding: "1px 7px", fontSize: 11, fontWeight: 700,
                                }}>{count}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Toolbar */}
                <div className="cl-toolbar-sticky" style={{ position: "static", boxShadow: "none", borderBottom: `1px solid ${T.border}` }}>
                    <div className="cl-search-bar" style={{ maxWidth: 340 }}>
                        <FiSearch size={15} style={{ marginLeft: 10, color: T.textLight, flexShrink: 0 }} />
                        <input
                            className="cl-search-input"
                            placeholder="Search by code or name…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search && (
                            <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: T.textLight, paddingRight: 10 }}>
                                <FiX size={14} />
                            </button>
                        )}
                    </div>

                    <div style={{ flex: 1 }} />

                    <button className="cl-btn-reset" onClick={load}>
                        <FiRefreshCw size={14} /> Refresh
                    </button>
                    <button className="cl-btn-add" onClick={openAdd}>
                        <FiPlus size={14} /> Add New Account
                    </button>
                </div>

                {/* Table */}
                <div className="cl-table-container">
                    <div className="cl-table-scroll">
                        <table className="cl-table">
                            <thead>
                                <tr>
                                    <th className="cl-th-sortable" onClick={() => toggleSort("account_code")}>
                                        Code <SortIcon active={sortKey === "account_code"} dir={sortDir} />
                                    </th>
                                    <th className="cl-th-sortable" onClick={() => toggleSort("account_name")}>
                                        Name <SortIcon active={sortKey === "account_name"} dir={sortDir} />
                                    </th>
                                    <th>Type</th>
                                    <th>Bank / Cash</th>
                                    <th>Status</th>
                                    <th style={{ width: 60 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={6} className="cl-empty">Loading accounts…</td></tr>
                                ) : pageRows.length === 0 ? (
                                    <tr><td colSpan={6} className="cl-empty">
                                        {search || activeType !== "All"
                                            ? "No accounts match your filters."
                                            : "No accounts yet. Click \"Add New Account\" to create one."}
                                    </td></tr>
                                ) : pageRows.map((r) => {
                                    const def = typeDef(r.account_type);
                                    return (
                                        <tr key={r.id} className="cl-table-row" onClick={() => openEdit(r)}>
                                            <td className="cl-td-strong">{r.account_code}</td>
                                            <td>{r.account_name}</td>
                                            <td>
                                                <span style={{
                                                    display: "inline-flex", alignItems: "center", gap: 6,
                                                    color: def.color, fontWeight: 600, fontSize: 13,
                                                }}>
                                                    <def.icon size={13} /> {r.account_type}
                                                </span>
                                            </td>
                                            <td>{r.is_bank_account
                                                ? <span style={{ color: T.success, fontWeight: 600, fontSize: 13 }}>Yes</span>
                                                : <span className="cl-td-muted">—</span>}
                                            </td>
                                            <td><StatusBadge value={r.is_active !== false ? "Active" : "Inactive"} /></td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={() => openEdit(r)}
                                                    title="Edit account"
                                                    style={{
                                                        border: `1px solid ${T.border}`, background: "#fff",
                                                        borderRadius: 7, width: 30, height: 30, cursor: "pointer",
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                        color: T.textMuted,
                                                    }}
                                                >
                                                    <FiEdit3 size={13} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="cl-pagination">
                        <span className="cl-rows-label">
                            Rows per page
                            <select className="cl-rows-select" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                                {ROWS_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </span>
                        <span className="cl-page-info">
                            {filtered.length === 0 ? "0 of 0" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, filtered.length)} of ${filtered.length}`}
                        </span>
                        <button className="cl-page-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                            <FiChevronLeft size={15} />
                        </button>
                        <button className="cl-page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                            <FiChevronRight size={15} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Add / Edit modal */}
            {modalOpen && (
                <div className="cl-modal-backdrop" onClick={() => !saving && setModalOpen(false)}>
                    <div className="cl-modal" style={{ maxWidth: 460, textAlign: "left" }} onClick={(e) => e.stopPropagation()}>
                        <button className="cl-modal-close" onClick={() => !saving && setModalOpen(false)}>
                            <FiX size={16} />
                        </button>

                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                            <div style={{
                                width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                                background: activeDef.color.replace(")", ",0.10)").replace("rgb", "rgba"),
                                color: activeDef.color,
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                                <activeDef.icon size={18} />
                            </div>
                            <div>
                                <div style={{ fontSize: T.fontSizeLg, fontWeight: 700, color: T.textPrimary }}>
                                    {editId ? "Edit Account" : "Add New Account"}
                                </div>
                                <div style={{ fontSize: T.fontSizeXs, color: T.textLight }}>
                                    {editId ? `Editing ${form.account_code || "account"}` : "Create a new ledger account"}
                                </div>
                            </div>
                        </div>

                        <div style={{ display: "flex", gap: 12 }}>
                            <FieldInput label="Account Code" name="account_code" value={form.account_code} onChange={onFieldChange} required placeholder="e.g. 1000/000" error={errors.account_code} />
                            <FieldInput label="Account Type" name="account_type" as="select" value={form.account_type} onChange={onFieldChange} required>
                                {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </FieldInput>
                        </div>

                        <FieldInput label="Account Name" name="account_name" value={form.account_name} onChange={onFieldChange} required placeholder="e.g. Rental Expense" error={errors.account_name} />

                        <FieldInput label="Parent Account" name="parent_id" as="select" value={form.parent_id} onChange={onFieldChange}>
                            <option value="">— No parent —</option>
                            {rows
                                .filter((r) => r.account_type === form.account_type && r.id !== editId)
                                .map((r) => (
                                    <option key={r.id} value={r.id}>{r.account_code} — {r.account_name}</option>
                                ))}
                        </FieldInput>

                        <label style={{
                            display: "flex", alignItems: "center", gap: 8,
                            fontSize: T.fontSizeSm, color: T.textPrimary, fontWeight: 500,
                            marginBottom: 14, cursor: "pointer", userSelect: "none",
                        }}>
                            <input
                                type="checkbox"
                                checked={form.is_bank_account}
                                onChange={(e) => setForm({ ...form, is_bank_account: e.target.checked })}
                                style={{ width: 15, height: 15, accentColor: T.accent, cursor: "pointer" }}
                            />
                            Is Bank / Cash Account?
                            <span style={{ fontSize: T.fontSizeXs, color: T.textLight, fontWeight: 400 }}>
                                (shows in the "Pay From" list on payment vouchers)
                            </span>
                        </label>

                        {editId && (
                            <label style={{
                                display: "flex", alignItems: "center", gap: 8,
                                fontSize: T.fontSizeSm, color: T.textPrimary, fontWeight: 500,
                                marginBottom: 20, cursor: "pointer", userSelect: "none",
                            }}>
                                <input
                                    type="checkbox"
                                    checked={form.is_active}
                                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                    style={{ width: 15, height: 15, accentColor: T.accent, cursor: "pointer" }}
                                />
                                Active
                            </label>
                        )}

                        <div className="cl-modal-actions">
                            <button className="cl-modal-btn-cancel" onClick={() => setModalOpen(false)} disabled={saving}>
                                Cancel
                            </button>
                            <button
                                className="cl-modal-btn-confirm cl-modal-btn-confirm-success"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? "Saving…" : editId ? "Save Changes" : "Create Account"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function SortIcon({ active, dir }) {
    return (
        <span className={`cl-sort-icon ${active ? "active" : ""}`}>
            {active && dir === "desc" ? <FiChevronDown size={12} /> : <FiChevronUp size={12} />}
        </span>
    );
}